import { PublicationError } from '@streamlain/doc-publication-contract';
import {
  localAccountKeyError,
  type LocalDataKind,
  LocalDataSealer,
} from '@streamlain/view-config/local-data-seal';

import {
  type LocalDataKeyOperation,
  LocalDataKeyProvider,
} from '../../local-data/key-provider';
import {
  containerScope,
  decodeLocalContainer,
  encodeLocalContainer,
  type LocalDataContainer,
  type LocalDocKeyRecord,
} from '../../local-data/payload';
import { isPlainLocalData } from '../../local-data/seal';
import type {
  LocalDataWriteContext,
  NativePayloadIdentity,
} from '../../storage/doc-publication';
import type { NativeDBConnection, SqliteNativeDBOptions } from './db';

export class SqliteKeyCASConflict extends Error {
  constructor() {
    super('KEY_CAS_CONFLICT');
  }
}

export class SqliteEncryptionContext {
  operation: LocalDataKeyOperation | null = null;
  readonly registries = new Map<string, LocalDocKeyRecord>();
  readonly candidates = new Set<string>();
  readonly keys = new Map<string, { keyId: string; key: CryptoKey }>();
  constructor(readonly owner: SqliteEncryption) {}
  async capture(scopes: readonly string[], writing: boolean) {
    if (this.owner.plain) return;
    const rows = await this.owner.connection.apis.readLocalDataScopes(
      this.owner.options.authBinding,
      [...new Set(scopes)]
    );
    this.operation = await this.owner.provider!.beginOperation();
    await Promise.all(
      rows.map(async row => {
        if (!row.record && row.hasPayload)
          throw localAccountKeyError('KEY_UNAVAILABLE');
        if (!row.record && !writing) return;
        const record =
          row.record ??
          (await this.owner.provider!.candidate(
            row.keyScopeId,
            this.operation!
          ));
        if (!row.record) this.candidates.add(row.keyScopeId);
        this.registries.set(row.keyScopeId, record);
        this.keys.set(
          row.keyScopeId,
          await this.owner.provider!.getKey(
            record,
            row.keyScopeId,
            !!row.record,
            this.operation!
          )
        );
      })
    );
  }
  get context(): LocalDataWriteContext | undefined {
    if (this.owner.plain) return;
    return {
      registries: [...this.registries].map(([id, record]) => ({
        record,
        expectedRegistryRevision: this.candidates.has(id)
          ? null
          : record.registryRevision,
      })),
    };
  }
  private key(id: string) {
    const key = this.keys.get(id);
    if (!key) throw localAccountKeyError('KEY_UNAVAILABLE');
    return key;
  }
  async sealContainer(
    keyScopeId: string,
    docId: string,
    kind: LocalDataKind,
    bytes: Uint8Array
  ): Promise<LocalDataContainer> {
    const revision = crypto.randomUUID();
    const sealed = await new LocalDataSealer({
      getKey: async () => this.key(keyScopeId),
    }).seal({ ...this.owner.identity(), docId, kind, revision }, bytes);
    return { keyScopeId, revision, sealed };
  }
  async seal(
    keyScopeId: string,
    docId: string,
    kind: LocalDataKind,
    bytes: Uint8Array
  ) {
    return {
      bin: encodeLocalContainer(
        await this.sealContainer(keyScopeId, docId, kind, bytes)
      ),
      payloadIdentity: {
        keyScopeId,
        storageRevision: crypto.randomUUID(),
      } satisfies NativePayloadIdentity,
    };
  }
  async open(
    keyScopeId: string,
    docId: string,
    kind: LocalDataKind,
    bytes: Uint8Array
  ) {
    return this.openContainer(
      keyScopeId,
      docId,
      kind,
      decodeLocalContainer(bytes)
    );
  }
  async openContainer(
    keyScopeId: string,
    docId: string,
    kind: LocalDataKind,
    payload: LocalDataContainer
  ) {
    if (
      payload.keyScopeId !== keyScopeId ||
      payload.sealed.keyId !== this.registries.get(keyScopeId)?.keyId
    )
      throw localAccountKeyError('CORRUPT');
    return new LocalDataSealer({
      getKey: async () => this.key(keyScopeId),
    }).open(
      containerScope({ ...this.owner.identity(), docId, kind }, payload),
      payload.sealed
    );
  }
  /** Retain only the authority token after a completed index batch/rebuild. */
  releasePreparedKeys() {
    this.keys.clear();
    this.registries.clear();
    this.candidates.clear();
    // beginOperation owns this snapshot Map, independently of the provider LRU.
    const cached = this.operation?.cached;
    if (cached instanceof Map) cached.clear();
  }
  private async assertOperation() {
    if (!this.owner.plain)
      await this.owner.provider!.assertOperation(this.operation);
  }
  async assert() {
    await this.assertOperation();
    if (!this.owner.plain) {
      // The SQLite mirror is authoritative too (including a newer epoch on another handle).
      await this.owner.connection.apis.readLocalDataScopes(
        this.owner.options.authBinding,
        []
      );
    }
  }
  async commit<T>(
    work: (context: LocalDataWriteContext | undefined) => Promise<T>
  ): Promise<T> {
    // Native auth() is checked atomically with payload/registry writes.
    await this.assertOperation();
    const result = await work(this.context);
    for (const id of this.candidates) {
      const record = this.registries.get(id),
        key = this.keys.get(id);
      if (record && key) this.owner.provider!.remember(record, key.key);
    }
    return result;
  }
}

export class SqliteEncryption {
  readonly plain: boolean;
  readonly provider: LocalDataKeyProvider | null;
  constructor(
    readonly options: SqliteNativeDBOptions,
    readonly connection: NativeDBConnection
  ) {
    const binding = options.authBinding;
    this.plain =
      binding !== undefined &&
      ((options.flavour === 'local' && binding === null) ||
        (options.flavour !== 'local' &&
          !!binding &&
          binding.accountId.startsWith('external:'))) &&
      isPlainLocalData({ accountKey: binding?.accountId ?? 'local' }, binding);
    this.provider =
      binding && !this.plain ? new LocalDataKeyProvider(binding) : null;
  }
  validate() {
    const b = this.options.authBinding;
    if (
      b === undefined ||
      (this.options.flavour === 'local') !== (b === null) ||
      (b &&
        (!b.accountId ||
          !b.workspaceId ||
          !Number.isSafeInteger(b.authEpoch) ||
          b.authEpoch < 1))
    )
      throw new PublicationError('LOCKED');
  }
  identity() {
    this.validate();
    const b = this.options.authBinding;
    if (!b) throw localAccountKeyError('LOCKED');
    return { accountId: b.accountId, workspaceId: b.workspaceId };
  }
  async capture(scopes: readonly string[], writing: boolean) {
    this.validate();
    const context = new SqliteEncryptionContext(this);
    await context.capture(scopes, writing);
    return context;
  }
  async write<T>(work: () => Promise<T>): Promise<T> {
    try {
      return await work();
    } catch (error) {
      if (!(error instanceof SqliteKeyCASConflict)) throw error;
      try {
        return await work();
      } catch (next) {
        if (next instanceof SqliteKeyCASConflict)
          throw new PublicationError('LOCAL_CONFLICT');
        throw next;
      }
    }
  }
  dispose() {
    this.provider?.dispose();
  }
}
